/**
 * Common CompactDealRail component used by other modules as a reusable component which returns the ui for a product carousel.
 *
 * @module views/components/CompactDealRail
 * @memberof -Common
 */
import './CompactDealRail.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Image from '@ulta/core/components/Image/Image';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

import CompactDealCard from '../CompactDealCard/CompactDealCard';
import ProductHighlighter from '../ProductHighlighter/ProductHighlighter';

/**
  * Represents a CompactDealRail component
  *
  * @method
  * @param {CompactDealRailProps} props - React properties passed from composition
  * @returns CompactDealRail
  */
export const CompactDealRail = function( props ){
  const compactDealRailRef = useRef( null );
  const { breakpoint } = useDeviceInflection();

  const {
    /* Cards */
    componentKey,
    dataCapture,
    isGrid,
    items = defaultProps.items,
    spotlight,

    /* Rail Title */
    title = defaultProps.title,
    viewAllAction,

    /* Rail meta data */
    countLabel = defaultProps.countLabel,
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    nextAccessibility = defaultProps.nextAccessibility,
    nextClickAction,
    previousAccessibility = defaultProps.previousAccessibility,
    previousClickAction
  } = props ;

  const isHighlighter = !!(
    spotlight &&
    spotlight.eyebrow &&
    spotlight.title &&
    spotlight.subtitle &&
    spotlight.graphicElement?.graphicImage?.imageUrl
  );

  /* Setting variables */
  let formattedGraphicElement;
  let formattedSpotlightCard = {};
  let productCards = items || [];

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  /* Checks if Rail is in a small device */
  const isSmallDevice = breakpoint.isSmallDevice();

  const isHighlighterRail = ( isHighlighter && isSmallDevice );
  const isHighlighterRailDesktop = ( isHighlighter && !isSmallDevice );

  if( isHighlighterRail ){
    productCards = items.slice( 1 );
  }

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const _railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const _railMeta = {
    countLabel: countLabel,
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  // if we need the metaData for SEO or DC purposes, we will have to pass this in as an external media object. In that case, palette Spotlight will need a small update to accept an externalMediaElement
  if( isHighlighter ){
    formattedGraphicElement = (
      <Image
        src={ spotlight.graphicElement.graphicImage.imageUrl }
        metaData={ spotlight.graphicElement.graphicImage.metaData }
        alt='decorative'
      />
    );
    formattedSpotlightCard = (
      <div className='CompactDealRail__highlightedProduct'>
        <ProductHighlighter
          { ...spreadProps( { ...items[0] } ) }
          spotlight={ spotlight }
          componentKey={ componentKey }
          formattedGraphicElement={ formattedGraphicElement }
        />
      </div>
    );
  }

  if( !hasItems( productCards ) ){
    return null;
  }

  return (
    <div
      className={
        classNames( 'CompactDealRail', {
          [`CompactDealRail--hasSpotlight`]: isHighlighterRailDesktop
        } )
      }
      ref={ compactDealRailRef }
    >
      <GridContainer>
        <Rail
          railRef={ compactDealRailRef }
          id={ railUuid }
          items={ productCards }
          previousClickAction={ previousClickAction }
          nextClickAction={ nextClickAction }
          headline={ title }
          headlineTag='h2'
          showCountLabel={ true }
          viewAllAction={ viewAllAction }
          isHighlighter={ isHighlighter }
          externalElementMobileSpotlightCard={ formattedSpotlightCard }
          externalSmallDevice={ isSmallDevice }
          spotlight={ spotlight }
          dataCapture={ dataCapture }
          _railADAmeta={ _railADAmeta }
          _railMeta={ _railMeta }
          sliderPerViewXL={ 4 }
          slidesPerGroupXL={ 4 }
          sliderPerViewLG={ 4 }
          sliderHighlightPerViewLG={ 3 }
          slidesPerGroupLG={ 4 }
          slidesHighlightPerGroupLG={ 3 }
          sliderPerViewMD={ 3 }
          slidesPerGroupMD={ 3 }
          slidesPerGroupSM={ 1 }
          sliderPerViewSM={ 1.5 }
        >
          { isHighlighterRailDesktop &&
            <Slide>
              <div className='CompactDealRail__highlightedContent'>
                <div className='CompactDealRail__contentWrapper'>
                  <div className='CompactDealRail__eyebrow'>
                    <Text
                      htmlTag='p'
                      textStyle='eyebrow'
                      textAlign='right'
                    >
                      { spotlight.eyebrow }
                    </Text>
                  </div>
                  <div className='CompactDealRail__title'>
                    <Text
                      htmlTag='p'
                      textStyle='title-6'
                      textAlign='right'
                    >
                      { spotlight.title }
                    </Text>
                  </div>
                  <div className='CompactDealRail__subtitle'>
                    <Text
                      htmlTag='p'
                      textStyle='subtitle-2'
                      textAlign='right'
                    >
                      { spotlight.subtitle }
                    </Text>
                  </div>
                </div>
                <div className='CompactDealRail__horizontalLine'>
                  <Image
                    src={ spotlight.graphicElement.graphicImage.imageUrl }
                  />
                </div>
              </div>
            </Slide>
          }
          { productCards?.length > 0 && productCards.map( ( product, index ) => (
            <Slide key={ index }>
              <div key={ product.skuId + '_' + index }>
                <CompactDealCard
                  images={ product.images }
                  eyebrow={ product.eyebrow }
                  headline={ product.headline }
                  subHeadline={ product.subHeadline }
                  tag={ product.tag }
                  itemClickAction={ product.itemClickAction }
                  isGrid={ isGrid }
                />
              </div>
            </Slide>
          ) ) }
        </Rail>
      </GridContainer>
    </div>
  );
};

/**
  * Default values for passed properties
  * @type {object}
  * @property {string} countLabel='' - The default value of the count label is empty string.
  * @property {array} items=[] - The default value for items is set to empty array.
  * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string.
  * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string.
  * @property {string} title='' - The default value of the title is empty string.
  */
export const defaultProps = {
  countLabel: '',
  items: [],
  nextAccessibility: '',
  previousAccessibility: '',
  title: ''
};

/**
 * Property type definitions
 * @typedef CompactDealRailProps
 * @type {object}
 * @property {string} carouselAccessibility - Value for aria label for product rail
 * @property {string} carouselCardAccessibility - Value for the product card aria label
 * @property {string} carouselDetailsAccessibility - Value for aria label for carousel
 * @property {string} componentKey - Sets the component key
 * @property {string} countLabel - Sets the count label for CompactDealRail list
 * @property {boolean} isGrid - Boolean value used to display list layout
 * @property {Array.<{promoText: string, reviewCount: Number, rating: Number, brandName: string, productName: string, variantLabel: string, badge: string, image: string, availability: array, addToBagAccessibility: string, additionalOffersText: string, altImage: string, bookmarkAccessibility: string, bookmarked: boolean, kitPrice: string, listPrice: string, salePrice: string, skuId: string}>} items - List of products to loop over and display in carousel.
 * @property {string} nextAccessibility - Sets the aria label for next button.
 * @property {object} nextClickAction - The prop that holds the action for the next button.
 * @property {string} previousAccessibility - Sets the aria label for previous button.
 * @property {object} previousClickAction - The prop that holds the action for the previous button
 * @property {object} spotlight - The spotlight content for the rail.
 * @property {string} title - Sets the title for CompactDealRail list.
 * @property {object} viewAllAction - This is the action object with action label and url.
 */
export const propTypes = {
  /**
   * Value for aria label for product rail
   */
  carouselAccessibility: PropTypes.string,
  /**
   * Value for the product card aria label
   */
  carouselCardAccessibility: PropTypes.string,
  /**
   * Value for aria label for carousel
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * Sets the component key
   */
  componentKey: PropTypes.string,
  /**
   * The prop that holds the count label for the product list.
   */
  countLabel: PropTypes.string,
  /**
   * Boolean value used to display list layout
   */
  isGrid: PropTypes.bool,
  /**
   * The prop that holds the list of products to render.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape( {
      promoText: PropTypes.string,
      reviewCount: PropTypes.number,
      rating: PropTypes.number,
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantLabel: PropTypes.string,
      badge: PropTypes.string,
      image: PropTypes.object,
      availability: PropTypes.arrayOf( PropTypes.string ),
      listPrice: PropTypes.string,
      salePrice: PropTypes.string,
      kitPrice: PropTypes.string,
      bookmarkAccessibility: PropTypes.string,
      addToBagAccessibility: PropTypes.string,
      additionalOffersText: PropTypes.string,
      altImage: PropTypes.string,
      bookmarked: PropTypes.bool,
      skuId: PropTypes.string
    } )
  ),
  /**
   * The prop that holds the aria label for next button.
   */
  nextAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the next button.
   */
  nextClickAction: PropTypes.object,
  /**
   * The prop that holds the aria label for previous button.
   */
  previousAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the previous button
   */
  previousClickAction: PropTypes.object,
  /**
   * The spotlight content for the rail.
   */
  spotlight: PropTypes.object,
  /**
   * The prop that holds title for the carousel.
   */
  title: PropTypes.string,
  /**
   * This is the action with label and url.
   */
  viewAllAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string

  } )
};

CompactDealRail.propTypes = propTypes;
CompactDealRail.defaultProps = defaultProps;

export default CompactDealRail;